import { AUTH_CONSTANTS, createAuthTimeoutError } from '../constants/auth.constants.js'
import { authService } from '../services/auth.service.js'

/**
 * Mixin que protege carregamento de dados em páginas autenticadas.
 *
 * Garante que nenhuma chamada à API ocorra antes do estado de autenticação
 * Firebase estar resolvido. Implementa cancelamento automático via AbortController
 * quando a página é removida do DOM durante navegação rápida.
 *
 * Fluxo:
 * 1. `connectedCallback` → chama `_loadWithAuthGuard(loadFn)`
 * 2. Aguarda `authService.waitForAuth()` com timeout de 10s
 * 3. Se autenticado: executa `loadFn()` imediatamente na mesma cadeia assíncrona
 * 4. Se não autenticado: mantém zero chamadas, `loading=false`
 * 5. Se timeout: `loading=false`, exibe mensagem de erro
 * 6. Se desconectado antes de auth resolver: cancela tudo via AbortController
 *
 * @param {typeof import('lit').LitElement} superClass - Classe base Lit Element
 * @returns {typeof import('lit').LitElement} Classe com proteção de auth timing
 *
 * @example
 * import { ProtectedPageMixin } from '../core/mixins/protected-page.mixin.js'
 *
 * class MinhaPage extends ProtectedPageMixin(LitElement) {
 *   connectedCallback () {
 *     super.connectedCallback()
 *     this._loadWithAuthGuard(() => this._carregarDados())
 *   }
 * }
 */
export const ProtectedPageMixin = (superClass) => class ProtectedPageMixin extends superClass {
  /**
   * Controller para cancelamento de operações pendentes.
   * Utilizado para abortar a espera de auth quando a página é
   * removida do DOM antes da resolução.
   *
   * @type {AbortController|null}
   */
  _authAbortController = null

  /**
   * Carrega dados com proteção de auth timing.
   *
   * Aguarda resolução do estado de autenticação (máximo 10 segundos)
   * antes de executar a função de carregamento fornecida. Implementa
   * cancelamento via AbortController para evitar efeitos colaterais
   * após desconexão do DOM.
   *
   * Comportamentos:
   * - Se autenticado: executa `loadFn()` imediatamente na mesma cadeia assíncrona
   * - Se não autenticado: zero chamadas à API, `loading=false`
   * - Se timeout (10s): `loading=false`, exibe mensagem de erro via `authError`
   * - Se desconectado: cancela tudo via AbortController, sem efeitos colaterais
   *
   * @param {Function} loadFn - Função assíncrona de carregamento de dados
   * @returns {Promise<void>} Resolve quando carregamento finaliza ou é cancelado
   *
   * @example
   * async _carregarDados () {
   *   const dados = await apiService.get('transacoes')
   *   this.transacoes = dados
   * }
   *
   * connectedCallback () {
   *   super.connectedCallback()
   *   this._loadWithAuthGuard(() => this._carregarDados())
   * }
   */
  async _loadWithAuthGuard (loadFn) {
    this.loading = true
    this._authAbortController = new AbortController()
    const { signal } = this._authAbortController

    try {
      // Aguarda auth com timeout de 10s usando Promise.race()
      // A primeira Promise que resolver/rejeitar vence a corrida
      await Promise.race([
        authService.waitForAuth(),
        new Promise((_, reject) => {
          const timeoutId = setTimeout(
            () => reject(createAuthTimeoutError()),
            AUTH_CONSTANTS.AUTH_TIMEOUT
          )
          // Se abortado antes do timeout, limpa timer e rejeita com AbortError
          signal.addEventListener('abort', () => {
            clearTimeout(timeoutId)
            reject(new DOMException('Aborted', 'AbortError'))
          })
        })
      ])

      // Verifica cancelamento após auth resolver (navegação rápida)
      if (signal.aborted) return

      // Verifica se está autenticado — se não, zero chamadas à API
      if (!authService.isAuthenticated()) {
        this.loading = false
        return
      }

      // Executa função de carregamento na mesma cadeia assíncrona (sem delay)
      await loadFn()
    } catch (error) {
      // Cancelado por desconexão do DOM — sem efeitos colaterais
      if (error.name === 'AbortError') return

      // Timeout de autenticação — exibe mensagem de erro
      if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
        this.loading = false
        this.authError = 'Falha na verificação de autenticação. Tente recarregar a página.'
        return
      }

      // Outros erros: propaga para tratamento externo
      throw error
    } finally {
      // Garante loading=false se não foi cancelado por abort
      if (!signal.aborted) {
        this.loading = false
      }
    }
  }

  /**
   * Lifecycle hook de desconexão do DOM.
   * Cancela qualquer carregamento pendente via AbortController para
   * evitar chamadas à API após a página ser removida (navegação rápida).
   *
   * Garante que nenhum efeito colateral ocorra após desconexão,
   * mesmo que o auth resolva posteriormente.
   */
  disconnectedCallback () {
    super.disconnectedCallback()
    if (this._authAbortController) {
      this._authAbortController.abort()
      this._authAbortController = null
    }
  }
}
